import { makeStyles, Typography } from "@material-ui/core"
import clsx from "clsx"
import React from "react"

import DBTChevronLeftIcon from "./DBTChevronLeftIcon"
import DBTChevronRightIcon from "./DBTChevronRightIcon"
import { colors } from "../../../utils/theme"

export interface PageControlItem<TValue = unknown> {
  displayName: string
  value: TValue
}

export interface PageControlProps<TValue = unknown> {
  items: PageControlItem<TValue>[]
  /**
   * Controlled value for the current page. When provided the component becomes controlled.
   */
  value?: TValue
  /**
   * Initial value for the uncontrolled mode. Falls back to the first item when omitted.
   */
  defaultValue?: TValue
  /**
   * Emits the value we navigated to along with helpful metadata.
   */
  onChange?: (
    value: TValue,
    payload: { index: number; item: PageControlItem<TValue> }
  ) => void
  /**
   * Optional className for external layout tweaks.
   */
  className?: string
  /**
   * Optional content to render next to the current item's display name.
   * Useful for contextual controls like view toggles.
   */
  children?: React.ReactNode
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(2),
  },
  button: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    padding: 0,
    transition: "color 120ms ease",
    "&:hover svg path": {
      stroke: colors.primary.shadow,
    },
    "&:focus": {
      outline: "none",
    },
  },
  buttonHidden: {
    visibility: "hidden",
    pointerEvents: "none",
  },
  title: {
    color: colors.neutral.space,
    textAlign: "center",
    fontWeight: 700,
    width: 350,
    fontSize: 18,
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}))

function PageControl<TValue = unknown>({
  items,
  value,
  defaultValue,
  onChange,
  className,
  children,
}: PageControlProps<TValue>) {
  const classes = useStyles()

  const findIndexByValue = React.useCallback(
    (target?: TValue) => {
      if (target === undefined) {
        return -1
      }

      return items.findIndex((item) => item.value === target)
    },
    [items]
  )

  const [internalIndex, setInternalIndex] = React.useState<number>(() => {
    if (!items.length) {
      return -1
    }

    const fromValue = value !== undefined ? findIndexByValue(value) : -1
    if (fromValue >= 0) {
      return fromValue
    }

    const fromDefault =
      defaultValue !== undefined ? findIndexByValue(defaultValue) : -1
    if (fromDefault >= 0) {
      return fromDefault
    }

    return 0
  })

  const isControlled = value !== undefined

  React.useEffect(() => {
    if (isControlled) {
      return
    }

    setInternalIndex((previousIndex) => {
      if (!items.length) {
        return -1
      }

      if (previousIndex < 0) {
        return 0
      }

      const lastIndex = items.length - 1
      return previousIndex > lastIndex ? lastIndex : previousIndex
    })
  }, [isControlled, items])

  const currentIndex = React.useMemo(() => {
    if (!items.length) {
      return -1
    }

    if (isControlled) {
      const controlledIndex = findIndexByValue(value)
      return controlledIndex >= 0 ? controlledIndex : 0
    }

    return internalIndex
  }, [findIndexByValue, internalIndex, isControlled, items.length, value])

  const currentItem = currentIndex >= 0 ? items[currentIndex] : undefined
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex >= 0 && currentIndex < items.length - 1

  const handleNavigate = React.useCallback(
    (direction: 1 | -1) => {
      if (currentIndex < 0) {
        return
      }

      const candidateIndex = currentIndex + direction
      if (candidateIndex < 0 || candidateIndex > items.length - 1) {
        return
      }

      if (!isControlled) {
        setInternalIndex(candidateIndex)
      }

      const nextItem = items[candidateIndex]
      onChange?.(nextItem.value, { index: candidateIndex, item: nextItem })
    },
    [currentIndex, isControlled, items, onChange]
  )

  if (!currentItem) {
    return null
  }

  return (
    <div className={clsx(classes.root, className)}>
      <button
        type="button"
        aria-label="Go to previous item"
        className={clsx(classes.button, !hasPrevious && classes.buttonHidden)}
        onClick={() => handleNavigate(-1)}
        disabled={!hasPrevious}
        aria-hidden={!hasPrevious}
        tabIndex={hasPrevious ? 0 : -1}
      >
        <DBTChevronLeftIcon />
      </button>

      <div className={classes.titleContainer}>
        <Typography variant="body1" className={classes.title} component="span">
          {currentItem.displayName}
        </Typography>
        {children}
      </div>

      <button
        type="button"
        aria-label="Go to next item"
        className={clsx(classes.button, !hasNext && classes.buttonHidden)}
        onClick={() => handleNavigate(1)}
        disabled={!hasNext}
        aria-hidden={!hasNext}
        tabIndex={hasNext ? 0 : -1}
      >
        <DBTChevronRightIcon />
      </button>
    </div>
  )
}

export default PageControl
